/**
 * Simplex lazy loader — loads the Simplex checkout SDK ON DEMAND, post page load.
 *
 * The Simplex embed normally runs as inline <script> tags that execute at parse
 * time (see template-parts/blocks/buy-widget-embeds/simplex-embed.php, eager
 * mode). That cannot be deferred to a user action, so this module reproduces the
 * same sequence programmatically for the cases where Simplex must appear AFTER
 * page load:
 *   - the secondary Simplex tab in the buy-widget-tabs block (loads on tab click)
 *   - the "try our other provider" fallback after a Transak failure (Phase 1.3)
 *
 * ⚠️ UNVERIFIED: whether the Simplex SDK can initialise post-load at all is not
 * proven and is NOT locally testable (no browser; the sandbox needs this domain
 * whitelisted with Simplex). This loader is built to the SDK's documented eager
 * sequence. If it cannot init post-load, the FAILURE PATH below emits
 * `simplex_lazy_init_failed` with the STAGE that broke, so staging verification
 * (B2 item 5) yields a diagnosis — CSP-ish (script_load), SDK sequencing (init),
 * or container timing (create_form) — not just a pass/fail. If it fails, the
 * 1.3 fallback is invalid too — that is a STOP, not a workaround.
 *
 * @see template-parts/blocks/buy-widget-embeds/simplex-embed.php  (defer mode markup + config)
 * @see src/js/tracking/buy-widget-funnel.js                        (funnel: simplex_* events)
 */

/** Give up if an injected script never fires load/error. */
const SCRIPT_LOAD_TIMEOUT_MS = 8000;

/** Stuck watchdog window — matches the eager embed + Transak (DECISIONS D0.1). */
const STUCK_TIMEOUT_MS = 12000;

/** Module-level guard: never inject a given Simplex script URL more than once. */
const injected = new Map(); // url -> Promise<void>

/**
 * Module-level guard: only ONE Simplex checkout may initialise per page. The SDK
 * targets a fixed global id (#checkout-element), so a second init would collide.
 * Set true on the first successful (or attempted) createForm; a later loader —
 * from a different block/instance — must abort rather than corrupt the first.
 */
let simplexActivated = false;

/**
 * Inject one <script> once; resolve on load, reject on error/timeout.
 * @param {string} src
 * @param {boolean} asyncAttr
 * @returns {Promise<void>}
 */
function injectScript(src, asyncAttr) {
    if (injected.has(src)) return injected.get(src);

    const p = new Promise((resolve, reject) => {
        // Reuse a tag already in the DOM (e.g. eager embed present elsewhere).
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) {
            if (existing.dataset.cgLoaded === '1') return resolve();
            existing.addEventListener('load', () => resolve(), { once: true });
            existing.addEventListener('error', () => reject(new Error(`script error: ${src}`)), { once: true });
            return;
        }

        const el = document.createElement('script');
        el.src = src;
        if (asyncAttr) el.async = true;

        const timer = setTimeout(() => reject(new Error(`script timeout: ${src}`)), SCRIPT_LOAD_TIMEOUT_MS);
        el.addEventListener('load', () => {
            clearTimeout(timer);
            el.dataset.cgLoaded = '1';
            resolve();
        }, { once: true });
        el.addEventListener('error', () => {
            clearTimeout(timer);
            reject(new Error(`script error: ${src}`));
        }, { once: true });

        document.head.appendChild(el);
    });

    injected.set(src, p);
    return p;
}

export default class SimplexLoader {

    /**
     * @param {Object} config - from the deferred embed's JSON blob.
     * @param {string} config.publicKey
     * @param {string} config.sdkJs
     * @param {string} config.formSdkJs
     * @param {string} config.splxJs
     * @param {string} config.version
     * @param {string} [config.abProvider]
     * @param {string} [config.buyCountry]
     */
    constructor(config) {
        this.config = config || {};
        this._loaded = false;
        this._settled = false;   // checkout succeeded/failed/rendered → stuck watchdog void
        this._stuckTimer = null;
        this._observer = null;   // MutationObserver watching #checkout-element for the iframe
    }

    /** Emit the staged failure signal so staging can diagnose WHERE it broke. */
    _fail(stage, err, loadStart) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event:          'simplex_lazy_init_failed',
            stage,          // 'duplicate_container' | 'script_load' | 'init' | 'create_form'
            country:        this.config.buyCountry || 'XX',
            page_path:      window.location.pathname,
            elapsed_ms:     loadStart !== null ? Math.round(performance.now() - loadStart) : null,
        });
        console.error(`[SimplexLoader] failed at stage="${stage}"`, err);
    }

    /**
     * Load + render Simplex into the already-present #checkout-element.
     * Idempotent: a second call after success is a no-op.
     *
     * @returns {Promise<boolean>} true on success, false on any staged failure.
     */
    async load() {
        if (this._loaded) return true;

        const loadStart = (window.performance && performance.now) ? performance.now() : null;
        const { publicKey, sdkJs, formSdkJs, splxJs } = this.config;

        // Defend the single-instance invariant, don't just assume it. A second
        // live #checkout-element (eager hero + tabs on one page) or an
        // already-succeeded init would collide with the SDK's fixed global id —
        // abort with a diagnosable event instead of silently corrupting the first
        // widget. NB: only a SUCCESSFUL init sets the flag (see below), so a failed
        // attempt never locks out a legitimate retry (e.g. fallback after a tab
        // load fails).
        if (simplexActivated || document.querySelectorAll('#checkout-element').length > 1) {
            this._fail('duplicate_container', new Error('a Simplex #checkout-element is already active on this page'), loadStart);
            return false;
        }

        // The SDK calls this global once sdk.js is ready. Define BEFORE injecting.
        window.simplexAsyncFunction = () => {
            try {
                if (typeof Simplex !== 'undefined') Simplex.init({ public_key: publicKey });
            } catch (e) {
                // Surfaced by the init-stage check below; swallow here so the SDK
                // callback itself doesn't throw uncaught.
                this._initError = e;
            }
        };

        // ── stage: script_load ────────────────────────────────────────────────
        try {
            // sdk.js is async in the eager embed; form-sdk.js + splx.js are
            // parser-ordered there. Load sdk first, then the other two.
            await injectScript(sdkJs, true);
            await injectScript(formSdkJs, false);
            await injectScript(splxJs, false);
        } catch (err) {
            this._fail('script_load', err, loadStart);
            return false;
        }

        // ── stage: init ───────────────────────────────────────────────────────
        // window.simplexAsyncFunction should have run during sdk.js load. If the
        // SDK global never appeared, or init threw, this is an init-stage failure.
        if (typeof Simplex === 'undefined' && !window.simplex) {
            this._fail('init', new Error('Simplex global missing after sdk load'), loadStart);
            return false;
        }
        if (this._initError) {
            this._fail('init', this._initError, loadStart);
            return false;
        }

        // ── stage: create_form ─────────────────────────────────────────────────
        if (!window.simplex || typeof window.simplex.createForm !== 'function') {
            this._fail('create_form', new Error('window.simplex.createForm unavailable'), loadStart);
            return false;
        }

        // Subscribe to the SDK lifecycle BEFORE createForm — a fast
        // checkoutLoadSucceeded must not fire before we're listening.
        this._subscribe(loadStart);

        try {
            window.simplex.createForm();
        } catch (err) {
            this._fail('create_form', err, loadStart);
            return false;
        }

        this._loaded = true;
        simplexActivated = true;   // success — block any later loader on this page

        // Arm the stuck watchdog AND a DOM-based success detector. The watchdog
        // must be cancelled the moment the widget actually renders; we do NOT rely
        // solely on the SDK's checkoutLoadSucceeded event (observed to be
        // unreliable in the loader — it left the watchdog armed and fired a false
        // stuck over a working widget). The iframe appearing in #checkout-element
        // is an independent, reliable "not stuck" signal.
        this._armStuckWatchdog(loadStart);
        return true;
    }

    /**
     * Subscribe to the Simplex SDK lifecycle. Called BEFORE createForm so no early
     * event is missed. Pushes the same funnel events as the eager embed. On
     * success/failure it settles the watchdog (see _settle). Intentionally mirrors
     * simplex-embed.php (eager) rather than sharing (DECISIONS D1.9/D1.11).
     */
    _subscribe(loadStart) {
        window.dataLayer = window.dataLayer || [];

        // Start marker (createForm about to run). NOT a "loaded" signal.
        window.dataLayer.push({ event: 'simplex_checkout_loaded' });

        const sdk = window.simplex || (typeof Simplex !== 'undefined' ? Simplex : null);
        if (!sdk || typeof sdk.subscribe !== 'function') return;

        sdk.subscribe('checkoutLoadSucceeded', () => {
            this._settle(); // cancel the stuck watchdog on genuine success
            if (loadStart !== null) {
                window.dataLayer.push({
                    event:   'simplex_load_time',
                    load_ms: Math.round(performance.now() - loadStart),
                });
            }
        });
        sdk.subscribe('checkoutLoadFailed', () => {
            this._settle(); // a real failure is reported via simplex_load_failed, not "stuck"
            window.dataLayer.push({ event: 'simplex_load_failed' });
        });
        sdk.subscribe('onlineFlowFinished', (e) => {
            window.dataLayer.push({
                event:          'simplex_flow_finished',
                simplex_result: (e && e.payload && e.payload.result) || 'unknown',
            });
        });
    }

    /**
     * Arm the stuck watchdog + an independent DOM success detector.
     *
     * The watchdog fires `simplex_widget_stuck` only if NOTHING has settled it
     * within STUCK_TIMEOUT_MS. It is settled by (a) the SDK checkoutLoadSucceeded/
     * Failed events, OR (b) the Simplex iframe appearing in #checkout-element — a
     * reliable "the widget rendered" signal that does not depend on the SDK event
     * firing. Without (b), a working widget on a slow (~12s) load falsely tripped
     * the watchdog → false fallback.
     */
    _armStuckWatchdog(loadStart) {
        const container = document.getElementById('checkout-element');

        // If the iframe is already present (very fast load), we're done.
        if (container && container.querySelector('iframe')) {
            this._settle();
            return;
        }

        if (container && 'MutationObserver' in window) {
            this._observer = new MutationObserver(() => {
                if (container.querySelector('iframe')) {
                    this._settle(); // rendered → not stuck
                }
            });
            this._observer.observe(container, { childList: true, subtree: true });
        }

        this._stuckTimer = setTimeout(() => {
            if (this._settled) return;
            this._settle(); // stop observing; we've made our call
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event:      'simplex_widget_stuck',
                elapsed_ms: loadStart !== null ? Math.round(performance.now() - loadStart) : null,
                country:    this.config.buyCountry || 'XX',
                page_path:  window.location.pathname,
            });
        }, STUCK_TIMEOUT_MS);
    }

    /** Mark the load settled: cancel the stuck watchdog + stop observing. Idempotent. */
    _settle() {
        this._settled = true;
        if (this._stuckTimer) { clearTimeout(this._stuckTimer); this._stuckTimer = null; }
        if (this._observer)   { this._observer.disconnect(); this._observer = null; }
    }
}
